import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Check, LockKeyhole, Star, UserRound, UsersRound, Loader2 } from "lucide-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { frontendApi, guestApi } from "@/lib/api";
import { saveBookingDraft } from "@/lib/bookingDraft";
import { useProperty } from "@/hooks/useApiData";
import { useSession } from "@/hooks/useSession";

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) =>
  value?.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const money = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value || 0);

export default function BookingReview() {
  const { propertySlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { property, loading } = useProperty(propertySlug);
  const { user, checking: sessionChecking } = useSession();
  const [submitting, setSubmitting] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [additionalGuestNames, setAdditionalGuestNames] = useState([]);

  const checkInValue = searchParams.get("check_in") || "";
  const checkOutValue = searchParams.get("check_out") || "";
  const guests = Math.max(1, Number(searchParams.get("guests") || 1));
  const checkIn = parseDate(checkInValue);
  const checkOut = parseDate(checkOutValue);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    setAdditionalGuestNames((current) =>
      Array.from({ length: Math.max(0, guests - 1) }, (_, index) => current[index] || "")
    );
  }, [guests]);

  const fillFromAccount = () => {
    if (!user) return;
    setGuestName(user.name || "");
    setGuestEmail(user.email || "");
    setGuestPhone(user.phone || "");
  };

  useEffect(() => {
    if (user && !guestName && !guestEmail && !guestPhone) fillFromAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const pricing = useMemo(() => {
    const nights =
      checkIn && checkOut
        ? Math.max(0, Math.round((checkOut - checkIn) / 86400000))
        : 0;
    const nightly = Number(property?.price_per_night ?? property?.price ?? 0);
    const total = nightly * nights;

    return { nights, nightly, total };
  }, [checkIn, checkOut, property]);

  const invalidStay =
    !checkIn ||
    !checkOut ||
    checkIn < today ||
    pricing.nights < 1 ||
    !property ||
    guests > Number(property?.max_guests ?? property?.guests ?? 1);

  const guestDetailsValid = guestName.trim().length > 1 && guestEmail.trim().length > 3;

  const confirmAndPay = async () => {
    if (invalidStay || submitting) return;
    if (!guestDetailsValid) {
      toast.error("Please enter the primary guest name and email before continuing.");
      return;
    }

    setSubmitting(true);

    try {
      const availabilityResponse = await frontendApi.checkPropertyAvailability(propertySlug, {
        check_in: checkInValue,
        check_out: checkOutValue,
        guests,
      });
      const availability =
        availabilityResponse?.unwrapped ?? availabilityResponse?.data ?? availabilityResponse;

      if (availability?.available === false) {
        toast.error(availability?.message || "These dates are no longer available.");
        setSubmitting(false);
        return;
      }

      const authoritativeTotal = Number(availability?.quote?.total ?? pricing.total);
      const authoritativeNights = Number(availability?.quote?.nights ?? pricing.nights);
      const namedAdditionalGuests = additionalGuestNames.map((name) => name.trim()).filter(Boolean);

      const draft = {
        propertyId: property.id,
        propertySlug,
        propertyName: property.name,
        propertyImage: property.images?.[0] || "",
        location: property.location,
        checkIn: checkInValue,
        checkOut: checkOutValue,
        guests,
        guestName: guestName.trim(),
        guestEmail: guestEmail.trim(),
        guestPhone: guestPhone.trim(),
        additionalGuestNames: namedAdditionalGuests,
        nights: authoritativeNights,
        total: authoritativeTotal,
      };
      saveBookingDraft(draft);

      const response = await guestApi.createCheckoutSession({
        property_id: property.numericId || property.id,
        property_slug: propertySlug,
        check_in: checkInValue,
        check_out: checkOutValue,
        guests,
        guest_name: guestName.trim(),
        guest_email: guestEmail.trim(),
        guest_phone: guestPhone.trim() || undefined,
        additional_guest_names: namedAdditionalGuests,
      });
      const data = response?.unwrapped ?? response?.data ?? response;
      const checkoutUrl = data?.checkout_url || data?.url;

      if (!checkoutUrl) throw new Error("Checkout URL was not returned.");
      window.location.assign(checkoutUrl);
    } catch (error) {
      if (error?.response?.status === 401) {
        navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
        return;
      }
      if (error?.response?.status === 409 || error?.response?.status === 422) {
        toast.error(error?.apiMessage || "Those dates are no longer available. Please choose another stay period.");
      } else {
        toast.error(error?.apiMessage || error?.message || "Unable to start checkout.");
      }
      setSubmitting(false);
    }
  };

  if (loading || sessionChecking) {
    return (
      <main className="mx-auto min-h-[70vh] max-w-[1200px] px-5 py-16 md:px-10">
        <div className="animate-pulse space-y-8">
          <div className="h-4 w-36 rounded bg-muted" />
          <div className="h-16 w-2/3 rounded bg-muted" />
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="h-[520px] rounded bg-muted" />
            <div className="h-[420px] rounded bg-muted" />
          </div>
        </div>
      </main>
    );
  }

  if (!property || invalidStay) {
    return (
      <main className="mx-auto min-h-[70vh] max-w-[760px] px-5 py-24 text-center md:px-10 md:py-32">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Booking review</p>
        <h1 className="mt-4 font-display text-5xl tracking-tight">Your stay details are incomplete.</h1>
        <p className="mx-auto mt-5 max-w-xl text-muted-foreground">
          Return to the property and select future dates and a valid guest count before continuing.
        </p>
        <Link to={`/properties/${propertySlug}`} className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground">
          <ArrowLeft size={15} /> Back to property
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-[1200px] px-5 py-10 md:px-10 md:py-16">
      <Link to={`/properties/${propertySlug}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">
        <ArrowLeft size={14} /> Change stay details
      </Link>

      <div className="mt-8 grid gap-10 lg:grid-cols-[1.08fr_0.92fr] lg:gap-14">
        <section>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Review before payment</p>
          <h1 className="mt-3 font-display text-5xl font-medium tracking-tight md:text-7xl">Confirm your stay.</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
            Add the names travelling on this reservation and review everything below before Stripe opens.
          </p>

          <div className="mt-9 border border-foreground/10 bg-card p-5 md:p-7">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground"><UserRound size={14} /> Primary guest</p>
                <p className="mt-2 text-sm text-muted-foreground">This person will be the main guest on the reservation.</p>
              </div>
              {user && (
                <button type="button" onClick={fillFromAccount} className="rounded-full border border-foreground/15 px-4 py-2 text-xs font-medium hover:border-foreground/35">
                  Autofill from account
                </button>
              )}
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <label className="sm:col-span-2">
                <span className="text-xs font-medium text-muted-foreground">Full name *</span>
                <input value={guestName} onChange={(event) => setGuestName(event.target.value)} autoComplete="name" className="mt-1.5 w-full border border-foreground/15 bg-background px-4 py-3 text-sm outline-none focus:border-primary" placeholder="Primary guest full name" />
              </label>
              <label>
                <span className="text-xs font-medium text-muted-foreground">Email *</span>
                <input type="email" value={guestEmail} onChange={(event) => setGuestEmail(event.target.value)} autoComplete="email" className="mt-1.5 w-full border border-foreground/15 bg-background px-4 py-3 text-sm outline-none focus:border-primary" placeholder="guest@example.com" />
              </label>
              <label>
                <span className="text-xs font-medium text-muted-foreground">Phone</span>
                <input value={guestPhone} onChange={(event) => setGuestPhone(event.target.value)} autoComplete="tel" className="mt-1.5 w-full border border-foreground/15 bg-background px-4 py-3 text-sm outline-none focus:border-primary" placeholder="Phone number" />
              </label>
            </div>
          </div>

          {guests > 1 && (
            <div className="mt-5 border border-foreground/10 bg-card p-5 md:p-7">
              <p className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground"><UsersRound size={14} /> Additional guests</p>
              <p className="mt-2 text-sm text-muted-foreground">Add the other guest names now. You can leave a field blank if the name is not known yet.</p>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {additionalGuestNames.map((name, index) => (
                  <label key={index}>
                    <span className="text-xs font-medium text-muted-foreground">Guest {index + 2}</span>
                    <input
                      value={name}
                      onChange={(event) => setAdditionalGuestNames((current) => current.map((item, itemIndex) => itemIndex === index ? event.target.value : item))}
                      className="mt-1.5 w-full border border-foreground/15 bg-background px-4 py-3 text-sm outline-none focus:border-primary"
                      placeholder={`Guest ${index + 2} full name`}
                    />
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 border-y border-foreground/10">
            <div className="grid gap-2 border-b border-foreground/10 py-5 sm:grid-cols-[150px_1fr]">
              <span className="text-sm text-muted-foreground">Reservation</span>
              <strong className="font-medium">{formatDate(checkIn)} – {formatDate(checkOut)} · {pricing.nights} night{pricing.nights === 1 ? "" : "s"}</strong>
            </div>
            <div className="grid gap-2 border-b border-foreground/10 py-5 sm:grid-cols-[150px_1fr]">
              <span className="text-sm text-muted-foreground">Guests</span>
              <strong className="font-medium">{guests} guest{guests === 1 ? "" : "s"}</strong>
            </div>
            <div className="grid gap-2 py-5 sm:grid-cols-[150px_1fr]">
              <span className="text-sm text-muted-foreground">Cancellation</span>
              <div>
                <strong className="font-medium">Property cancellation policy applies</strong>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">Review the applicable deadlines and house rules before completing payment.</p>
              </div>
            </div>
          </div>

          <div className="mt-7 space-y-3 text-sm text-muted-foreground">
            <p className="flex gap-3"><Check className="mt-0.5 shrink-0 text-primary" size={17} /> Availability and price are checked again immediately before Stripe Checkout opens.</p>
            <p className="flex gap-3"><LockKeyhole className="mt-0.5 shrink-0 text-primary" size={17} /> Your payment is completed securely through Stripe Checkout.</p>
          </div>
        </section>

        <aside>
          <div className="border border-foreground/10 bg-card p-6 md:sticky md:top-28 md:p-8">
            <div className="flex gap-4 border-b border-foreground/10 pb-6">
              {property.images?.[0] && <img src={property.images[0]} alt="" className="h-24 w-28 shrink-0 object-cover" />}
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{property.location}</p>
                <h2 className="mt-2 font-display text-2xl leading-tight">{property.name}</h2>
                <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground"><Star size={12} className="fill-foreground stroke-foreground" /> {Number(property.rating || 0).toFixed(2)}</p>
              </div>
            </div>

            <div className="mt-6 grid gap-3 rounded-lg bg-muted/40 p-4 text-sm">
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Check-in</span><span className="text-right font-medium">{formatDate(checkIn)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Check-out</span><span className="text-right font-medium">{formatDate(checkOut)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Primary guest</span><span className="max-w-[190px] truncate text-right font-medium">{guestName || "Add guest name"}</span></div>
            </div>

            <h3 className="mt-6 font-display text-2xl">Price details</h3>
            <div className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between"><span>{money(pricing.nightly)} × {pricing.nights} nights</span><span>{money(pricing.total)}</span></div>
              <div className="flex justify-between border-t border-foreground/10 pt-4 font-medium"><span>Total USD</span><span className="font-display text-2xl">{money(pricing.total)}</span></div>
            </div>

            <button type="button" onClick={confirmAndPay} disabled={submitting || !guestDetailsValid} className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary py-4 text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50">
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Rechecking availability…</> : "Proceed to secure payment"}
            </button>
            <p className="mt-4 text-center text-xs leading-relaxed text-muted-foreground">Review complete? Stripe will open only after the latest availability check succeeds.</p>
          </div>
        </aside>
      </div>
    </main>
  );
}
